import { ReportLanguage, Report, RabEntry } from "../types";
import {
  normalizeRabEntry,
  parseAiResponse,
  extractBestArray,
  stripRegulationParagraphs,
  normalizeBox,
} from "./geminiNormalizers";
import { UFSA_FALLBACK_CATALOG } from "./ufsaFallbackCatalog";
import { salvageJsonArray, isTruncatedAiResponse, rawAiText } from "./aiJsonSalvage";
import { AI_RETRY_DELAYS_MS, describeAiError, withAiRetry } from "./aiRetry";
import { REGELWERK_KOPF, entscheideVerworfeneRegelwerke, leseVerworfeneRegelwerke } from "./regelwerkHinweis";
import { SCHREIBREGEL_AKTIONEN, bereinigeKiAntwort } from "../../supabase/functions/_shared/regelwerk";
// Kontextbau und Antwortpruefung der drei KI-Assistenten liegen als REINE
// Funktionen daneben (aiAssistContext.ts) und sind dort geprueft.
import { lesePlanAssistAntwort, leseKoordinierungAntwort, leseHandbuchAntwort, leseBauordnungAntwort, planungKontext } from "./aiAssistContext";
import type {
  AiAssistErgebnis,
  PlanAssistKontext,
  PlanAssistAntwort,
  KoordinierungAssistKontext,
  KoordinierungAssistAntwort,
  HandbuchAssistKontext,
  HandbuchAssistAntwort,
  BauordnungAssistKontext,
  BauordnungLeseErgebnis,
  PlanungEingabe,
  PlanungKontext,
} from "./aiAssistContext";

const SUPABASE_FUNC_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/gemini-proxy`;

/**
 * Der EINE Weg zum gemini-proxy: Session-Token holen, POST mit {action,
 * payload}, Fehlertext aus der Antwort ziehen.
 *
 * Exportiert, damit weitere KI-Funktionen ausserhalb dieser Datei (z. B.
 * services/leadScoring.ts) dieselbe Aufrufkonvention benutzen, statt Auth-
 * und Fehlerbehandlung ein zweites Mal nachzubauen.
 */
export const callAiBackend = async (action: string, payload: any) => {
  const attempts = AI_RETRY_DELAYS_MS.length + 1;
  try {
    // Voruebergehende Stoerungen (Lastspitze bei Google, Ratenbegrenzung, 5xx)
    // werden wiederholt. Ohne das kostete ein Aussetzer von zwei Sekunden das
    // komplette Einlesen eines mehrseitigen Leistungsverzeichnisses — samt der
    // Minute, die Hochladen und Lesen gebraucht haben.
    const antwort = await withAiRetry(
      async () => {
        // Session des eingeloggten Benutzers; ohne Login der ANON_KEY.
        const { supabase } = await import('./supabaseClient');
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token || import.meta.env.VITE_SUPABASE_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY;

        const response = await fetch(SUPABASE_FUNC_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify({ action, payload }),
        });

        if (!response.ok) {
          let err = "Fehler bei der KI-Anfrage";
          try {
            const errData = await response.json();
            err = errData.error || err;
          } catch {
            err = `HTTP ${response.status}: ${response.statusText}`;
          }
          // Der Status wandert mit: der Proxy verpackt jeden Fehler als 400,
          // die Einstufung braucht aber beides — Text UND Status.
          const wrapped = new Error(err) as Error & { status?: number };
          wrapped.status = response.status;
          throw wrapped;
        }

        // Vom Proxy entfernte KI-Vorschriften (nicht bestätigt) samt ihrer
        // Stelle in der Antwort. Optional verkettet: Testattrappen ohne Kopf.
        const stellen = leseVerworfeneRegelwerke(response.headers?.get?.(REGELWERK_KOPF));
        return { daten: await response.json(), stellen };
      },
      {
        onRetry: (tryNumber, waitMs) => console.warn(
          `KI-Dienst ueberlastet (Aktion ${action}) — Versuch ${tryNumber} von ${attempts}, neuer Versuch in ${waitMs} ms.`,
        ),
      },
    );
    // Erst NACH dem Wiederholen und nur einmal: der Nutzer sieht je entfernter
    // Vorschrift den Eintrag, aus dem sie stammt, und kann sie selbst geprüft
    // zurücklegen (Dialog KiRegelwerkHinweis). Ohne Dialog kommt die Antwort
    // unverändert zurück. Wirft nie — der catch darunter bleibt den Netzfehlern.
    const daten = await entscheideVerworfeneRegelwerke(action, antwort.daten, antwort.stellen);
    // Schreibregel der fünf Module (keine Gedankenstriche) auch hier: ein
    // noch nicht neu ausgerollter Proxy liefert sie sonst ungefiltert.
    const woertlich = SCHREIBREGEL_AKTIONEN.get(action);
    return woertlich ? bereinigeKiAntwort(daten, woertlich) : daten;
  } catch (error) {
    console.error(`KI-Fehler bei Aktion ${action}:`, error);
    // Verstaendlicher Text statt der rohen englischen Dienstmeldung — die
    // Aufrufer zeigen `error.message` direkt im Dialog an.
    throw new Error(describeAiError((error as Error)?.message, attempts));
  }
};

// Converts image/document URLs to a clean Base64 string if an HTTP URL is supplied.
const ensureBase64 = async (urlOrBase64: string): Promise<string> => {
  if (!urlOrBase64) return '';
  if (urlOrBase64.startsWith('data:') || !urlOrBase64.startsWith('http')) {
    return urlOrBase64;
  }
  
  try {
    const response = await fetch(urlOrBase64);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status} fetching image`);
    }
    const blob = await response.blob();
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result as string);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn("Failed to fetch image directly, attempting canvas with anonymous crossOrigin:", error);
    return new Promise<string>((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = urlOrBase64;
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0);
            resolve(canvas.toDataURL('image/jpeg', 0.8));
          } else {
            reject(new Error("No canvas context"));
          }
        } catch (e) {
          reject(e);
        }
      };
      img.onerror = (e) => reject(new Error("External fallback image load failed: " + String(e)));
    });
  }
};

export const processVoiceTranscript = async (transcript: string): Promise<{ title: string; observation: string; responsible: string; priority: string }> => {
  try {
    const rawRes = await callAiBackend("processVoiceTranscript", { transcript });
    const parsed = parseAiResponse(rawRes);
    if (parsed && typeof parsed === 'object') {
      return {
        title: parsed.title || 'Beobachtung',
        observation: parsed.observation || transcript,
        responsible: parsed.responsible || '',
        priority: parsed.priority || 'Mittel'
      };
    }
  } catch (error) {
    console.error("Fehler bei processVoiceTranscript:", error);
  }
  return {
    title: "Beobachtung",
    observation: transcript,
    responsible: "",
    priority: "Mittel"
  };
};

/**
 * Meldung, wenn der Textvorschlag nicht kommt — Netz, Proxy, unlesbare oder
 * leere Antwort. Die Aufrufer zeigen sie so an (`error.message`).
 */
export const KI_NICHT_ERREICHBAR = 'KI nicht erreichbar. Bitte selbst formulieren oder später erneut versuchen.';

/**
 * Antwort des Textvorschlags lesen. `null` heisst: nichts Brauchbares — ohne
 * Beschreibung ist der Vorschlag leer, auch wenn ein Titel dasteht.
 */
export const leseSicherheitstext = (
  parsed: unknown,
  keyword: string,
): { title: string; observation: string; regulation: string } | null => {
  const obj: unknown = Array.isArray(parsed) ? parsed[0] : parsed;
  if (!obj || typeof obj !== 'object') return null;
  const feld = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
  const antwort = obj as Record<string, unknown>;
  const observation = feld(antwort.observation);
  if (!observation) return null;
  return {
    title: feld(antwort.title) || keyword,
    observation,
    regulation: stripRegulationParagraphs(feld(antwort.regulation)),
  };
};

/**
 * Textvorschlag aus einem Stichwort. Bei JEDEM Fehler wirft er
 * `KI_NICHT_ERREICHBAR` — frueher kam hier ein Platzhaltertext zurueck, der
 * wie ein echter Vorschlag aussah und die Eingabe des Nutzers ueberschrieb.
 */
export const generateSafetyText = async (
  keyword: string, 
  shortVersion: boolean = false,
  language: ReportLanguage = 'de',
  role: 'sigeko' | 'hse' = 'sigeko'
): Promise<{ title: string; observation: string; regulation: string }> => {
  let ergebnis: { title: string; observation: string; regulation: string } | null;
  try {
    const rawRes = await callAiBackend("generateSafetyText", { keyword, shortVersion, language, role });
    ergebnis = leseSicherheitstext(parseAiResponse(rawRes), keyword);
    if (!ergebnis) console.error("generateSafetyText: leere oder unlesbare Antwort", rawRes);
  } catch (error) {
    console.error("Fehler bei generateSafetyText:", error);
    throw new Error(KI_NICHT_ERREICHBAR, { cause: error });
  }
  if (!ergebnis) throw new Error(KI_NICHT_ERREICHBAR);
  return ergebnis;
};

export const fetchSafetyNews = async (): Promise<any[]> => {
  try {
    return await callAiBackend("fetchSafetyNews", {});
  } catch (error) {
    console.error("Fehler bei fetchSafetyNews:", error);
    return [];
  }
};

export const getFieldExplanation = async (topic: string): Promise<string> => {
  try {
    const rawRes = await callAiBackend("getFieldExplanation", { topic });
    // Der Proxy verlangt das Feld "text" (und verpackt unparsbare Antworten
    // ebenfalls als { text }). Gelesen wurde frueher nur "explanation" — die
    // Erklaerung kam dadurch immer leer an.
    return typeof rawRes === 'string' ? rawRes : (rawRes?.text || rawRes?.explanation || "");
  } catch (error) {
    console.error("Fehler bei getFieldExplanation:", error);
    return "";
  }
};

export const suggestBugFix = async (bug: {
  message: string;
  stack?: string;
  componentStack?: string;
  source: string;
  appContext?: string;
}): Promise<string> => {
  try {
    const res = await callAiBackend("suggestBugFix", bug);
    if (typeof res === 'string') return res;
    const parts = [
      res?.cause ? `Wahrscheinliche Ursache:\n${res.cause}` : '',
      res?.solution ? `Lösungsvorschlag:\n${res.solution}` : ''
    ].filter(Boolean);
    return parts.join('\n\n') || res?.text || "";
  } catch (error) {
    console.error("Fehler bei suggestBugFix:", error);
    return "";
  }
};

export const extractContactsFromImage = async (base64Data: string, mimeType: string): Promise<any[]> => {
  const finalBase = await ensureBase64(base64Data);
  const res = await callAiBackend("extractContactsFromImage", { base64Data: finalBase, mimeType });
  // Das Backend liefert je nach Modell-Antwort { contacts: [...] } oder ein
  // Top-Level-Array, bei abgeschnittener Antwort { text: "..." }. Ohne
  // Normalisierung scheitern Aufrufer an .length auf einem Objekt.
  return extractBestArray(parseAiResponse(res));
};

export const extractServicesFromDocument = async (base64Data: string, mimeType: string): Promise<any[]> => {
  const finalBase = await ensureBase64(base64Data);
  const res = await callAiBackend("extractServicesFromDocument", { base64Data: finalBase, mimeType });
  return extractBestArray(parseAiResponse(res));
};

// Funktion 1 — LV aus Ausschreibung: liefert die LV-Positionen
// ({ position, bezeichnung, einheit, menge, abschnitt }). Preise bewusst NICHT
// enthalten (der Anbieter kalkuliert selbst). extractBestArray normalisiert
// { positions: [...] } bzw. abgeschnittene { text }-Antworten.
//
// `truncated` kommt aus dem Proxy (finishReason MAX_TOKENS) und heisst: das
// Modell hat mitten im Dokument aufgehoert. Frueher fiel das niemandem auf —
// ein 40-seitiges LV kam als halbes LV an und sah vollstaendig aus.
export interface TenderLvExtraction {
  rows: any[];
  truncated: boolean;
}

export const extractTenderLV = async (base64Data: string, mimeType: string): Promise<TenderLvExtraction> => {
  const finalBase = await ensureBase64(base64Data);
  const res = await callAiBackend("extractTenderLV", { base64Data: finalBase, mimeType });

  // Die Abschneide-Marke MUSS aus der rohen Antwort gelesen werden.
  // parseAiResponse entpackt `.text` als erstes und wirft dabei alle
  // Geschwisterfelder weg — `_truncated` ging genau dann verloren, wenn es
  // gebraucht wurde: bei unparsbarem, weil abgeschnittenem JSON.
  const truncated = isTruncatedAiResponse(res);

  const parsed = parseAiResponse(res);
  const rows = extractBestArray(parsed);
  if (rows.length) return { rows, truncated };

  // Nichts Parsbares — aber im Rohtext stecken die vollstaendig gelesenen
  // Positionen bis zum Abbruch. Sie zu bergen ist der Unterschied zwischen
  // "280 Positionen, Rest fehlt" und "Keine Positionen erkannt".
  const salvaged = salvageJsonArray(
    typeof parsed === 'string' ? parsed : rawAiText(res),
    ['positions', 'positionen', 'items'],
  );
  // `truncated` bleibt allein die Marke des Proxys (finishReason MAX_TOKENS).
  // Eine geborgene Antwort ist nicht automatisch abgeschnitten — sie kann
  // auch nur unsauber verpackt gewesen sein, und ein falsches
  // "Unvollstaendig"-Banner schickt den Nutzer auf die Suche nach Zeilen,
  // die es nicht gibt.
  return { rows: salvaged, truncated };
};

/**
 * LV-Markiermodus, Stufe 2: markierte Zeilen in Felder ausrichten lassen.
 *
 * Anders als extractTenderLV geht hier KEIN Dokument an das Modell, sondern
 * nur die bereits ausgewaehlten Zeilen als Text. Das Finden — die Quelle der
 * verschluckten und erfundenen Positionen — entfaellt damit vollstaendig.
 *
 * Der Aufrufer darf einen Fehler ignorieren: die lokale Spaltenlesung ist
 * bereits ein vollstaendiges Ergebnis, das Ausrichten ist Kuer.
 */
/**
 * Historische Erkundung des A+S-Plans: Vorschlag erarbeiten lassen.
 *
 * Der Prompt wird in `components/asplan/ki/historischeErkundung.ts` gebaut und
 * ist dort geprueft — hier steht nur der Weg zum Backend. Die Antwort ist ein
 * VORSCHLAG und wird nie selbsttaetig gespeichert.
 *
 * Kennt der ausgerollte Proxy die Action noch nicht, meldet er „Unbekannte
 * Action"; der Aufrufer macht daraus einen verstaendlichen Hinweis, statt den
 * Rohtext zu zeigen.
 */
export const asplanHistorischeErkundung = async (
  promptText: string,
): Promise<Record<string, any>> => {
  return await callAiBackend("asplanHistorischeErkundung", { promptText });
};

export const alignLvRows = async (promptText: string): Promise<Record<string, any>> => {
  const res = await callAiBackend("alignLvRows", { promptText });
  const parsed = parseAiResponse(res);
  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed as Record<string, any>;
  // Abgeschnitten: die vollstaendig gelesenen Zeilen bergen, der Rest faellt
  // im Zusammenfuehren auf die lokale Lesung zurueck.
  const salvaged = salvageJsonArray(typeof parsed === 'string' ? parsed : rawAiText(res), ['zeilen', 'rows']);
  return salvaged.length ? { zeilen: salvaged } : {};
};

// Alt-/Fremdangebot einlesen: ein fertiger Angebotsbeleg -> Kopfdaten +
// Positionen MIT Preisen + ausgewiesene Endsummen. Ergebnisform siehe
// gemini-proxy case "extractQuoteDocument"; Weiterverarbeitung in
// quoteDocumentImport.parseQuoteDocument.
export interface QuoteDocumentExtraction {
  data: Record<string, any>;
  truncated: boolean;
}

export const extractQuoteDocument = async (
  base64Data: string,
  mimeType: string,
): Promise<QuoteDocumentExtraction> => {
  const finalBase = await ensureBase64(base64Data);
  const res = await callAiBackend("extractQuoteDocument", { base64Data: finalBase, mimeType });
  const parsed = parseAiResponse(res);
  const obj = (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) ? parsed as Record<string, any> : {};
  return { data: obj, truncated: !!obj._truncated };
};

// Funktion 2 — Anforderungen auslesen: liefert ein Objekt mit Projekt-Meta und
// Anforderungstext ({ bauvorhaben, strasse, plz, ort, baubeginn, bauende,
// intervall, begehungen, anforderungen }). Keine Positionen/Preise.
export const extractTenderRequirements = async (base64Data: string, mimeType: string): Promise<Record<string, any>> => {
  const finalBase = await ensureBase64(base64Data);
  const res = await callAiBackend("extractTenderRequirements", { base64Data: finalBase, mimeType });
  const parsed = parseAiResponse(res);
  return (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) ? parsed : {};
};

// Letzte Alt-Rechnung eines Bestandsprojekts einlesen: eigene Belegdaten +
// Abzugsblock (alle dort genannten Vorrechnungen). Ergebnisform siehe
// gemini-proxy case "extractLastInvoice"; Weiterverarbeitung in
// externalAdvances.extractedAdvancesFromAi.
export const extractLastInvoice = async (base64Data: string, mimeType: string): Promise<Record<string, any>> => {
  const finalBase = await ensureBase64(base64Data);
  const res = await callAiBackend("extractLastInvoice", { base64Data: finalBase, mimeType });
  const parsed = parseAiResponse(res);
  return (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) ? parsed : {};
};

// Kundenliste (Steuerberater-PDF) -> Stammdaten-Zeilen. Weiterverarbeitung
// in customerListImport (Abgleich per Kundennummer); Umsaetze bewusst nicht.
export const extractCustomerList = async (base64Data: string, mimeType: string): Promise<Record<string, any>> => {
  const finalBase = await ensureBase64(base64Data);
  const res = await callAiBackend("extractCustomerList", { base64Data: finalBase, mimeType });
  const parsed = parseAiResponse(res);
  return (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) ? parsed : {};
};

export const importOldReportFromPdf = async (base64Data: string): Promise<any> => {
  const finalBase = await ensureBase64(base64Data);
  return await callAiBackend("importOldReportFromPdf", { base64Data: finalBase });
};

export const analyzeSigePlan = async (base64Data: string, mimeType: string): Promise<any[]> => {
  const finalBase = await ensureBase64(base64Data);
  return await callAiBackend("analyzeSigePlan", { base64Data: finalBase, mimeType });
};

export const analyzeSigePlanDocument = async (base64Data: string, mimeType: string, promptText: string): Promise<any> => {
  const finalBase = await ensureBase64(base64Data);
  return await callAiBackend("analyzeSigePlanDocument", { base64Data: finalBase, mimeType, promptText });
};

export const generateSigePlanHazards = async (promptText: string): Promise<any> => {
  return await callAiBackend("generateSigePlanHazards", { promptText });
};

/** Ein gleichzeitig tätiges Gewerk — Fenster und Dauer aus dem Bauzeitenplan. */
export interface GleichzeitigesGewerk {
  name: string;
  /** ISO-Tagesdatum. */
  von: string;
  bis: string;
  tage: number;
}

/**
 * Ein Gewerk, wie es in die Vorhersage gegenseitiger Gefährdungen geht.
 *
 * `gleichzeitigMit` ist OPTIONAL und stammt aus dem Koordinierungsmodul. Fehlt
 * es, ist der Aufruf derselbe wie bisher — der Zeitraum ist dann schlicht
 * unbekannt, und genau das soll die KI wissen, statt ihn zu raten.
 */
export interface MutualHazardTrade {
  /** Anzeigename = Achse der Gefährdungsmatrix (`Contact.company || role`). */
  name: string;
  /** Tätigkeit (`Contact.tradeType`) — sie trägt die fachliche Aussage. */
  activity: string;
  gleichzeitigMit?: GleichzeitigesGewerk[];
}

export const predictMutualHazards = async (tradesData: MutualHazardTrade[]): Promise<any[]> => {
  return await callAiBackend("predictMutualHazards", { tradesData });
};

export const generateRabSuggestion = async (component: string): Promise<Partial<RabEntry>> => {
  try {
    const promptText = `Du bist ein erfahrener SiGeKo-Experte (Sicherheits- und Gesundheitsschutzkoordinator).
Generiere einen Vorschlag für die Gefährdungsbeurteilung und Instandhaltung (RAB / Regelwerk für die Instandhaltung) für folgendes Bauteil: "${component}".

Antworte AUSSCHLIESSLICH mit einem validen JSON-Objekt ohne zusätzlichen Text, ohne Markdown-Einleitung.
Das JSON-Objekt MUSS folgende Struktur haben:
{
  "component": "${component}",
  "category": "Gewerk/Bereich (z.B. Dach, Elektro, Sanitär, Fassade, Rohbau)",
  "activity": "Tätigkeit / Arbeitsschritt zur Wartung/Instandhaltung",
  "hazards": "Konkrete Gefährdungen / Risiken bei dieser Tätigkeit",
  "measures": "Erforderliche Schutzmaßnahmen (Sicherheitstechnisch, PSA, etc.)",
  "frequency": "Prüfintervall/Häufigkeit der Tätigkeiten (z.B. Jährlich, Vor Benutzung, alle 2 Jahre)",
  "regulations": "Einschlägige Regelwerke, nur real existierend und derzeit gültig, OHNE Paragraphen-, Absatz- oder Anhangsangabe (z.B. ArbStättV, DGUV Vorschrift 38); im Zweifel leerer String"
}
Antworte auf Deutsch.`;

    // Verwende den spezialisierten Supabase-Backend-Endpunkt für Einzeleinträge: 'generateUfsaEntry'
    const rawRes = await callAiBackend("generateUfsaEntry", { component, promptText });
    const parsed = parseAiResponse(rawRes);
    
    if (Array.isArray(parsed)) {
      return normalizeRabEntry(parsed[0]);
    }
    
    if (parsed && typeof parsed === 'object') {
      if (parsed.suggestion && typeof parsed.suggestion === 'object') {
        return normalizeRabEntry(parsed.suggestion);
      }
      if (parsed.entry && typeof parsed.entry === 'object') {
        return normalizeRabEntry(parsed.entry);
      }
      return normalizeRabEntry(parsed);
    }
  } catch (error) {
    console.error("Fehler bei generateRabSuggestion:", error);
  }
  return {};
};

/**
 * Ergebnis der Katalog-Erstellung fuer die Unterlage (RAB 32).
 *
 * `quelle` sagt, woher die Eintraege stammen: 'ki' ist die Antwort des
 * Modells auf die Beschreibung, 'ersatz' der feste Grundkatalog, weil die KI
 * nicht geantwortet hat. Frueher kamen im Fehlerfall still fuenf Eintraege
 * des Grundkatalogs zurueck — fuer den Aufrufer nicht von einer Auswertung
 * der Beschreibung zu unterscheiden. Die Oberflaeche muss sagen koennen,
 * dass es ein Ausfall war.
 */
export interface UfsaKatalogErgebnis {
  eintraege: Omit<RabEntry, 'id'>[];
  quelle: 'ki' | 'ersatz';
  /** Anzeigbare Meldung zum Ausfall; bei 'ki' null. */
  fehler: string | null;
}

/** So viele Eintraege des Grundkatalogs kommen als gekennzeichneter Ersatz. */
export const UFSA_ERSATZ_ANZAHL = 5;

/**
 * Prompt-Abschnitt mit den Angaben der Planungsphase fuer den UfsA-Katalog.
 *
 * Es gehen Bauaufgabe, Merkmale und Zahlen mit — NICHT die Baubeschreibung:
 * die Beschreibung des Gebaeudes tippt der Anwender fuer den Katalog selbst
 * ein, und zwei Beschreibungen desselben Vorhabens im Prompt widerspraechen
 * sich frueher oder spaeter. Die Merkmale kommen bereits gefiltert und
 * dreiwertig aus `planungKontext`; der Lesehinweis steht im JSON selbst.
 *
 * Ohne Planungsphase ein leerer String — der Prompt ist dann zeichengleich
 * mit dem bisherigen.
 */
export const ufsaPlanungAbschnitt = (planung: PlanungKontext | null): string => {
  if (!planung) return '';
  // Feld fuer Feld abgeschrieben, nicht weitergereicht — wie im Kontextbau.
  const angaben = {
    hinweis: planung.hinweis,
    merkmale: planung.merkmale,
    zahlen: planung.zahlen,
  };
  return 'Angaben des Koordinators aus der Planungsphase (JSON). Berücksichtige sie bei der Auswahl der '
    + 'Bauteile und Tätigkeiten für spätere Arbeiten — etwa Bestand, Dacharbeiten, Absturzhöhe, '
    + 'weiterlaufender Betrieb oder technische Gebäudeausrüstung:\n'
    + JSON.stringify(angaben);
};

export const generateCatalogFromDescription = async (
  description: string,
  existingEntries: RabEntry[] = [],
  planung?: PlanungEingabe | null,
): Promise<UfsaKatalogErgebnis> => {
  try {
    const existingList = existingEntries.length > 0
      ? `Bereits vorhandene Bauteile/Einträge (bitte KEINE doppelten Vorschläge für diese generieren, sondern nur NEUE, ergänzende Vorschläge):\n${existingEntries.map(e => `- ${e.component} (${e.activity})`).join('\n')}`
      : "";

    // Planungsphase (optional) VOR der Bestandsliste: sie beschreibt das
    // Vorhaben, die Liste nur, was schon dasteht. Leere Abschnitte fallen
    // weg, damit der Prompt ohne Phase unveraendert bleibt.
    const zusatz = [ufsaPlanungAbschnitt(planungKontext(planung)), existingList].filter(Boolean).join('\n\n');

    const promptText = `Du bist ein erfahrener SiGeKo-Experte (Sicherheits- und Gesundheitsschutzkoordinator).
Basierend auf der folgenden Projektbeschreibung für ein Bauvorhaben, identifiziere alle relevanten sicherheitskritischen Bauteile und Tätigkeiten zur Instandhaltung und erstelle einen Katalog für die Gefährdungsbeurteilung (RAB).

Projektbeschreibung:
"${description}"

${zusatz}

Antworte AUSSCHLIESSLICH mit einem validen JSON-Array ohne zusätzlichen Text, ohne Markdown-Einleitung.
Das JSON-Array MUSS eine Liste von Objekten mit folgender Struktur sein:
[
  {
    "component": "Name des Bauteils (z.B. Flachdachabläufe, Aufzugsanlage, Heizungstherme)",
    "category": "Gewerk/Bereich (z.B. Dach, Elektro, Sanitär, Fassade, Haustechnik)",
    "activity": "Tätigkeit zur Wartung oder Instandhaltung (z.B. Reinigung der Abläufe, Wartung des Aufzugs, Sichtprüfung)",
    "hazards": "Konkrete Gefährdungen bei dieser Tätigkeit (z.B. Absturz von der Dachkante, Stromschlag, Quetschen)",
    "measures": "Erforderliche Schutzmaßnahmen (z.B. Anschlagpunkte nutzen, Freischalten vor Arbeiten, PSA)",
    "frequency": "Häufigkeit/Intervalle (z.B. Halbjährlich, Jährlich)",
    "regulations": "Einschlägige Regelwerke, nur real existierend und derzeit gültig, OHNE Paragraphen-, Absatz- oder Anhangsangabe (z.B. ArbStättV, DGUV Vorschrift 38); im Zweifel leerer String"
  }
]
Erstelle MINDESTENS 10 und bis zu 30 realistische, hochqualitative Einträge basierend auf der Beschreibung und dem Gebäugetyp, die NOCH NICHT in der Liste der vorhandenen Einträge stehen. Zerlege das Gebäude in typische Gewerke und leite systematisch Bauteile ab.
Antworte auf Deutsch.`;

    // Verwende den spezialisierten Supabase-Backend-Endpunkt für Katalog-Erstellung: 'generateUfsaCatalog'
    const rawRes = await callAiBackend("generateUfsaCatalog", { description, promptText });
    const parsed = parseAiResponse(rawRes);
    const arr = extractBestArray(parsed);
    const aiEntries = arr.map(item => normalizeRabEntry(item)).filter(item => item && item.component);
    
    console.info(`KI hat ${aiEntries.length} Einträge generiert.`);
    
    // Verhindere absolute Duplikate (gleicher Component-Name)
    const uniqueEntriesMap = new Map();
    for (const e of aiEntries) {
       const key = e.component.toLowerCase().trim();
       if (!uniqueEntriesMap.has(key)) {
         uniqueEntriesMap.set(key, e);
       }
    }
    const finalEntries = Array.from(uniqueEntriesMap.values());

    return { eintraege: finalEntries, quelle: 'ki', fehler: null };
  } catch (error) {
    console.error("Fehler bei generateCatalogFromDescription:", error);
    // Der Grundkatalog kommt als gekennzeichneter ERSATZ, nicht als Ergebnis:
    // Ausfall darf nie wie eine Auswertung aussehen. `fehler` traegt die
    // fertige Meldung aus callAiBackend (deutsch, mit Handlungshinweis).
    const meldung = error instanceof Error && error.message.trim() ? error.message : KI_NICHT_ERREICHBAR;
    return { eintraege: UFSA_FALLBACK_CATALOG.slice(0, UFSA_ERSATZ_ANZAHL), quelle: 'ersatz', fehler: meldung };
  }
};

export const analyzeHazardImage = async (urlOrBase64: string, existingEntries: RabEntry[]): Promise<any> => {
  const base64Image = await ensureBase64(urlOrBase64);
  const cleanBase64 = base64Image.includes(';base64,') ? base64Image.split(';base64,')[1] : base64Image;

  const promptText = `Du bist ein erfahrener Sicherheits- und Gesundheitsschutzkoordinator (SiGeKo) und analysierst ein Baustellen- bzw. Bauteil-Foto für eine "Unterlage für spätere Arbeiten" (UfsA).

Untersuche das Bild systematisch und vollständig: gehe Vordergrund, Hintergrund, linken und rechten Rand sowie oben und unten getrennt durch.
Liste JEDE eigenständige Gefährdung als SEPARATES Objekt im "findings"-Array auf. Ein typisches Baustellenfoto enthält MEHRERE unterschiedliche Gefährdungen. Prüfe gezielt diese Kategorien und nimm jede zutreffende einzeln auf:
- Absturz (fehlender Seitenschutz, offene Kanten, Leitern, Gerüste, Öffnungen)
- Stolpern/Rutschen/Ordnung (lose Kabel, Materiallagerung, Verschmutzung)
- Persönliche Schutzausrüstung (fehlender Helm, Schuhe, Schutzbrille, Anschlagschutz)
- Elektrische Gefährdungen (offene Verteiler, beschädigte Leitungen)
- Mechanische/Quetsch-/Schnittgefahren (Maschinen, scharfe Kanten, herabfallende Teile)
- Bauliche Mängel/Statik (Risse, Korrosion, beschädigte Bauteile)

Regeln:
- Fasse NICHT mehrere Gefahren zu einer zusammen – jede einzeln.
- Erfinde NICHTS: Nimm nur auf, was tatsächlich im Bild erkennbar ist. Ist eine Kategorie nicht sichtbar, lass sie weg.
- Sind wirklich keine Gefährdungen erkennbar, gib ein leeres "findings"-Array zurück.

Für jede erkannte Gefahr liefere eine eng anliegende Bounding-Box als Prozentwerte 0–100 relativ zur Bildgröße (Ursprung oben links): "xmin"/"ymin" = obere linke Ecke, "xmax"/"ymax" = untere rechte Ecke.

Antworte AUSSCHLIESSLICH mit validem JSON in genau diesem Format (das "findings"-Array enthält ein Objekt PRO erkannter Gefährdung):
{
  "findings": [
    {
      "id": "finding-1",
      "title": "Kurzer, präziser Befundtitel",
      "description": "Konkrete Beschreibung der sichtbaren Gefährdung und der erforderlichen Schutzmaßnahme.",
      "severity": "low | medium | high",
      "box": { "xmin": 30, "xmax": 70, "ymin": 20, "ymax": 60 }
    },
    {
      "id": "finding-2",
      "title": "Weitere eigenständige Gefährdung",
      "description": "...",
      "severity": "medium",
      "box": { "xmin": 5, "xmax": 25, "ymin": 60, "ymax": 90 }
    }
  ],
  "suggestions": [
    {
      "component": "Betroffenes Bauteil",
      "category": "Gewerk/Bereich",
      "activity": "Tätigkeit zur Wartung/Instandhaltung",
      "hazards": "Konkrete Gefährdungen",
      "measures": "Erforderliche Schutzmaßnahmen",
      "frequency": "Prüfintervall/Häufigkeit",
      "regulations": "Einschlägige Regelwerke, nur real existierend und derzeit gültig, OHNE Paragraphen-, Absatz- oder Anhangsangabe; im Zweifel leerer String"
    }
  ]
}

Die "suggestions" sollen nur zu den tatsächlich erkannten Befunden passen. Antworte auf Deutsch.`;

  const rawRes = await callAiBackend("analyzeHazardImage", {
    base64Image: base64Image,
    base64Data: base64Image,
    base64ImageClean: cleanBase64,
    base64DataClean: cleanBase64,
    imageData: base64Image,
    image: cleanBase64,
    existingEntries,
    promptText
  });

  const parsed = parseAiResponse(rawRes);
  const rawFindings = Array.isArray(parsed?.findings) ? parsed.findings : [];

  // Echte KI-Befunde übernehmen und Boxen ins Editor-Format normalisieren.
  const findings = rawFindings.map((f: any, idx: number) => ({
    id: f?.id || `finding-${idx + 1}`,
    title: f?.title || f?.titel || 'Befund',
    description: f?.description || f?.beschreibung || '',
    severity: (f?.severity === 'low' || f?.severity === 'high') ? f.severity : 'medium',
    box: normalizeBox(f?.box)
  }));

  const suggestions = Array.isArray(parsed?.suggestions)
    ? parsed.suggestions.map((s: any) => normalizeRabEntry(s))
    : [];

  return { findings, suggestions };
};

export const analyzeHazardSpot = async (urlOrBase64: string): Promise<any> => {
  const base64Crop = await ensureBase64(urlOrBase64);
  const cleanBase64 = base64Crop.includes(';base64,') ? base64Crop.split(';base64,')[1] : base64Crop;

  const promptText = `Du bist ein erfahrener SiGeKo. Analysiere diesen markierten Bildausschnitt im Detail auf Sicherheitsmängel, Defekte oder Gefährdungen für die "Unterlage für spätere Arbeiten" (UfsA).
Beschreibe nur, was tatsächlich im Ausschnitt erkennbar ist.
Antworte AUSSCHLIESSLICH mit validem JSON in genau diesem Format (auf Deutsch):
{
  "title": "Präziser Befundtitel (z.B. Stark korrodiertes Geländer)",
  "description": "Konkrete Beschreibung des Mangels auf dem Bildausschnitt und empfohlene Schutzmaßnahme."
}`;

  const rawRes = await callAiBackend("analyzeHazardSpot", {
    base64Crop: base64Crop,
    base64Data: base64Crop,
    base64CropClean: cleanBase64,
    base64DataClean: cleanBase64,
    imageData: base64Crop,
    image: cleanBase64,
    promptText
  });

  const parsed = parseAiResponse(rawRes);
  if (parsed && typeof parsed === 'object') {
    // Backend-/Modellvarianten können deutsche Feldnamen liefern → tolerant lesen.
    const title = parsed.title || parsed.titel || parsed.gefahr || "Detailbefund";
    const description = parsed.description || parsed.beschreibung
      || [parsed.gefahr, parsed.massnahme].filter(Boolean).join(' – ')
      || "";
    return { title, description };
  }
  return { title: "Detailbefund", description: "" };
};

type TextMap = Record<string, string>;

// `items[].regulation` geht bewusst NICHT in die Übersetzung: Regelwerke sind
// Eigennamen ("ArbSchG", "DGUV Vorschrift 38"). Übersetzt erfand das Modell
// englische Bezeichnungen, die es als Vorschrift nicht gibt.
const TRANSLATE_PATHS = [
  'items[].title',
  'items[].observation',
  'items[].statusComment',
  'items[].location',
  'items[].responsiblePerson',
  'checklists[].text',
  'checklists[].comment',
];

export function extractTextMap(report: any): TextMap {
  const map: TextMap = {};
  for (const path of TRANSLATE_PATHS) {
    const arrMatch = path.match(/^(\w+)\[\]\.(\w+)$/);
    if (arrMatch) {
      const [, arrKey, field] = arrMatch;
      const arr = report?.[arrKey];
      if (Array.isArray(arr)) {
        arr.forEach((item, idx) => {
          const val = item?.[field];
          if (typeof val === 'string' && val.trim().length > 0) {
            map[`${arrKey}[${idx}].${field}`] = val;
          }
        });
      }
    } else {
      const parts = path.split('.');
      let cur: any = report;
      for (const p of parts) {
        cur = cur?.[p];
        if (cur === undefined) break;
      }
      if (typeof cur === 'string' && cur.trim().length > 0) {
        map[path] = cur;
      }
    }
  }
  return map;
}

export function applyTextMap(report: any, translated: TextMap): any {
  const clone = typeof (globalThis as any).structuredClone === 'function'
    ? (globalThis as any).structuredClone(report)
    : JSON.parse(JSON.stringify(report));

  for (const [path, value] of Object.entries(translated)) {
    if (typeof value !== 'string') continue;
    const arrMatch = path.match(/^(\w+)\[(\d+)\]\.(\w+)$/);
    if (arrMatch) {
      const [, arrKey, idxStr, field] = arrMatch;
      const idx = parseInt(idxStr, 10);
      if (clone[arrKey]?.[idx]) {
        clone[arrKey][idx][field] = value;
      }
    } else {
      const parts = path.split('.');
      let cur: any = clone;
      for (let i = 0; i < parts.length - 1; i++) {
        if (cur[parts[i]] === undefined) break;
        cur = cur[parts[i]];
      }
      if (cur) cur[parts[parts.length - 1]] = value;
    }
  }
  return clone;
}

// Simple, stable string hash (djb2) used to detect whether a report's source
// texts have changed since the last translation. Not cryptographic — only used
// as a cache key.
export function hashTextMap(map: TextMap): string {
  const s = JSON.stringify(map);
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = (((h << 5) + h) + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

// In-memory cache for the current session. Filled the first time a report is
// rendered/previewed in a target language, so the subsequent send reuses it.
const translationMemoryCache = new Map<string, TextMap>();

export const translateReportData = async (report: Report, targetLang: ReportLanguage): Promise<Report> => {
  if (targetLang === 'de') return report;
  const sourceMap = extractTextMap(report);
  if (Object.keys(sourceMap).length === 0) return report;

  // Cache key ties the translation to the exact source texts + target language.
  // If the texts change (new/edited findings), the key changes and we retranslate.
  const cacheKey = `${targetLang}:${hashTextMap(sourceMap)}`;

  // 1) Session cache (warm after the first preview/PDF in this language).
  let translated = translationMemoryCache.get(cacheKey);

  // 2) Persisted cache stored on the report metadata (survives reloads because
  //    it is saved within projects.data via saveProjectToCloud).
  if (!translated) {
    const persisted = (report.metadata as any)?.translationCache;
    if (persisted && persisted.key === cacheKey && persisted.translated) {
      translated = persisted.translated as TextMap;
      translationMemoryCache.set(cacheKey, translated);
    }
  }

  // 3) Cache miss -> translate once via the AI backend, then remember it both in
  //    memory and on the report so future PDFs/sends skip the expensive call.
  if (!translated) {
    const result = await callAiBackend("translateReportData", { texts: sourceMap, targetLang });
    translated = (result?.translated || result || {}) as TextMap;
    translationMemoryCache.set(cacheKey, translated);
    try {
      (report.metadata as any).translationCache = { key: cacheKey, translated };
    } catch (e) {
      // metadata not writable -> in-memory cache still applies
      console.warn('translateReportData: Übersetzungs-Cache konnte nicht am Report gespeichert werden:', e);
    }
  }

  return applyTextMap(report, translated);
};

// SiGePlan-Assistent
export const chatSigePlan = async (message: string, context: any): Promise<{ antwort: string; vorschlaege?: string[] }> => {
  return await callAiBackend("chatSigePlan", { message, context });
};

export const classifyTrade = async (firma: string, role?: string): Promise<{ gewerk: string; kategorie: string; konfidenz: number }> => {
  return await callAiBackend("classifyTrade", { firma, role });
};

export const generateWorkSteps = async (beschreibung: string, startdatum?: string): Promise<{ arbeitsschritte: any[] }> => {
  return await callAiBackend("generateWorkSteps", { beschreibung, startdatum });
};

export const validateSigePlan = async (plan: any): Promise<{ ergebnis: 'ok' | 'achtung' | 'kritisch'; befunde: any[]; zusammenfassung: string }> => {
  return await callAiBackend("validateSigePlan", { plan });
};

// ═══════════════════════════════════════════════════════════════════════════
// KI-ASSISTENTEN — drei Faelle, die NEBEN einem arbeitenden Modul stehen
// ═══════════════════════════════════════════════════════════════════════════
//
// `assistSigePlan`      — Fragen zum SiGe-Plan als GANZES (nicht der
//                          Gefaehrdungskatalog eines Schrittes; das bleibt
//                          `generateSigePlanHazards`).
// `assistKoordinierung` — zweite Stimme NEBEN dem Regelkatalog. Der Katalog
//                          bleibt die Grundlage: er rechnet ohne Netz (auf der
//                          Baustelle ist keins) und seine Fassung steht am
//                          Datensatz, damit in einem abgeschlossenen Projekt
//                          nachvollziehbar bleibt, warum eine Gefaehrdung dort
//                          stand. Eine KI-Antwort hat diese Eigenschaft nicht.
// `assistHandbuch`      — Rohtext fuer die Baustellenordnung und Hinweise auf
//                          Fehlstellen im Handbuch.
//
// Alle drei laufen ueber `callAiBackend` (Wiederholung bei Aussetzern) und
// geben ein GEPRUEFTES Ergebnis zurueck, statt zu werfen: die Assistenten
// stehen neben einem Modul, das ohne sie vollstaendig arbeitet. Ein
// geworfener Fehler muesste an drei Stellen gefangen werden, und wer ihn
// vergisst, reisst die ganze Ansicht mit. `ok: false` traegt denselben Text.
//
// WICHTIG: jeder Fall braucht seinen `case` im gemini-proxy UND einen Deploy
// (`supabase functions deploy gemini-proxy`) — sonst antwortet der Proxy mit
// "Unbekannte Action".

/**
 * Ruft eine Assistenten-Aktion auf und laesst die Antwort pruefen.
 *
 * Der Fehlerfall benutzt denselben Leser mit `null` — so entsteht die leere
 * Form genau einmal (im Leser) und nicht ein zweites Mal hier, wo sie beim
 * naechsten Feld auseinanderliefe.
 */
const rufeAssistenten = async <T>(
  action: string,
  payload: Record<string, unknown>,
  lese: (roh: unknown) => AiAssistErgebnis<T>,
): Promise<AiAssistErgebnis<T>> => {
  try {
    return lese(await callAiBackend(action, payload));
  } catch (error) {
    // `callAiBackend` hat den Text bereits verstaendlich gemacht
    // (describeAiError) — er wird hier nur durchgereicht.
    const leer = lese(null);
    return { ...leer, fehler: (error as Error)?.message || leer.fehler };
  }
};

/** Assistent im Plan-Reiter: was fehlt im Plan als Ganzem? */
export const assistSigePlan = async (
  kontext: PlanAssistKontext,
  frage: string,
): Promise<AiAssistErgebnis<PlanAssistAntwort>> =>
  rufeAssistenten('assistSigePlan', { kontext, frage }, lesePlanAssistAntwort);

/**
 * Zweite Stimme zur Koordinierung. ERGAENZT die Katalogvorschlaege und
 * ersetzt sie nicht; jeder Vorschlag traegt `quelle: 'ki'`.
 */
export const assistKoordinierung = async (
  kontext: KoordinierungAssistKontext,
): Promise<AiAssistErgebnis<KoordinierungAssistAntwort>> =>
  rufeAssistenten('assistKoordinierung', { kontext }, leseKoordinierungAntwort);

/** Textvorschlaege fuer die Baustellenordnung + Fehlstellen im Handbuch. */
export const assistHandbuch = async (
  kontext: HandbuchAssistKontext,
  frage: string = '',
): Promise<AiAssistErgebnis<HandbuchAssistAntwort>> =>
  rufeAssistenten('assistHandbuch', { kontext, frage }, leseHandbuchAntwort);

/**
 * Ergänzungen zur Baustellenordnung (Modul 06): was Katalog und Handbuch nicht
 * hergeben. Jeder Vorschlag trägt `quelle: 'ki'` und wird erst durch den
 * Menschen bestätigt.
 *
 * Eigener try/catch statt `rufeAssistenten`: dessen Rückgabe ist auf
 * `AiAssistErgebnis<T>` getypt, und die Zählung der verworfenen Vorschläge
 * (`verworfeneVorschlaege`, `verwerfungsgruende`) fiele dort aus dem Typ. Sie
 * muss aber bis in die Oberfläche durch — ein verworfener Vorschlag wird
 * gemeldet, nicht still weggelassen (Spec F.5). Der Ablauf ist derselbe:
 * Fehler → Leser mit `null`, Text aus `callAiBackend`.
 *
 * Die zulässigen Kapitelkennungen kommen aus dem Kontext selbst — es ist
 * genau die Liste, die das Modell gesehen hat.
 */
export const assistBauordnung = async (
  kontext: BauordnungAssistKontext,
  frage = '',
): Promise<BauordnungLeseErgebnis> => {
  try {
    const erlaubteKapitel = kontext.kapitel.map(k => k.kennung);
    return leseBauordnungAntwort(await callAiBackend('assistBauordnung', { kontext, frage }), erlaubteKapitel);
  } catch (error) {
    // `callAiBackend` hat den Text bereits verstaendlich gemacht
    // (describeAiError) — er wird hier nur durchgereicht.
    const leer = leseBauordnungAntwort(null, []);
    return { ...leer, fehler: (error as Error)?.message || leer.fehler };
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// SiFa — GEFAEHRDUNGSBEURTEILUNG (GBU): `gbuVorschlag` und `gbuPruefen`
// ═══════════════════════════════════════════════════════════════════════════
//
// Kontextbau, Antwortpruefung und Ersatz ohne KI liegen als reine Funktionen
// in sifa/gbuKi.ts (dort geprueft). Hier steht nur der Aufruf: ueber
// `callAiBackend` (Wiederholung bei Aussetzern, Dialog fuer verworfene
// Vorschriften) und mit ERGEBNIS statt throw — wie `rufeAssistenten`. Gelesen
// wird bewusst NICHT hier: der Leser braucht die Kurzzeichen-Zuordnung des
// Kontexts, und gbuKi.ts importiert diese Datei (kein Ring).
//
// Beide Aktionen sind ERZEUGEND und laufen im Proxy durch die
// Regelwerkspruefung. Jede Aenderung am `case` im gemini-proxy braucht einen
// Deploy der gebuendelten Ein-Datei-Fassung durch den Betreiber — sonst
// antwortet der Proxy mit "Unbekannte Action", und gbuKi.ts zeigt den Ersatz.

/** Rohe Proxy-Antwort oder der verstaendliche Fehlertext aus `callAiBackend`. */
export type GbuKiRohAntwort = { ok: true; roh: unknown } | { ok: false; fehler: string };

const rufeGbuKi = async (action: 'gbuVorschlag' | 'gbuPruefen', payload: { kontext: unknown; codes: unknown }): Promise<GbuKiRohAntwort> => {
  try {
    return { ok: true, roh: await callAiBackend(action, payload) };
  } catch (error) {
    // `callAiBackend` hat den Text bereits verstaendlich gemacht (describeAiError).
    const text = (error as Error)?.message;
    return { ok: false, fehler: typeof text === 'string' && text.trim() ? text : KI_NICHT_ERREICHBAR };
  }
};

/** K1: Gefaehrdungen, Massnahmen und Kopf fuer einen Arbeitsbereich (gbuKi.fordereGbuVorschlagAn). */
export const gbuVorschlag = (payload: { kontext: unknown; codes: unknown }): Promise<GbuKiRohAntwort> =>
  rufeGbuKi('gbuVorschlag', payload);

/** K2: Vollstaendigkeit einer GBU pruefen (gbuKi.pruefeGbuMitKi). */
export const gbuPruefen = (payload: { kontext: unknown; codes: unknown }): Promise<GbuKiRohAntwort> =>
  rufeGbuKi('gbuPruefen', payload);
